import { Injectable } from '@angular/core';
import { App, Color, File, HSLColor } from '../../models';

import { argbFromHex, Scheme, themeFromSourceColor, TonalPalette } from '@material/material-color-utilities';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { KhThemeServiceModel } from '../../models/kh-theme-service-model';
import { MapIcon } from '../../models/map-pin-model';
import { MAP_DEFAULTS } from '../../shared/constants';
import { CSSCalculatedVariables, MapPinStyles } from '../../shared/enums';
import { MapSettingsDataService } from '../data-services/map-settings-data.service';

@Injectable({
    providedIn: 'root'
})
export class KHThemeService {

    private _root = document.documentElement;
    private _isDarkMode = false;

    model: KhThemeServiceModel;

    contentHeight: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    color: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    name: string = environment.name;
    primaryColor: string = environment.colors.primaryColor;
    primaryTextColor: string = environment.colors.primaryTextColor;
    primaryBlackColor: string = environment.colors.primaryBlackColor;
    primaryLogo: string = environment.logos.primaryLogo;
    secondaryColor: string = environment.colors.secondaryColor;
    secondaryTextColor: string = environment.colors.secondaryTextColor;
    secondaryLogo: string = environment.logos.secondaryLogo;
    mapPin: string = null;
    mapPinHeight: number;
    mapPinStyle: number = MapPinStyles.Default;
    mapPinWidth: number;
    mapPinColor: Color;
    padding = 156;
    gridHeight = 0;
    svg = MAP_DEFAULTS.DEFAULT_MAP_PIN_URL;
    svgDocument: Document;
    lightTextColor = new Color(255, 255, 255, 1);
    darkTextColor = new Color(21, 24, 41, 1);
    defaultTextColor = this.lightTextColor;

    constructor(
        private _mapSettingsDataService: MapSettingsDataService
    ) {
        this.initModel();
        this.resetTheme();
        this.calculateContentHeight();
    }

    initModel(): void {
        if (this.model === undefined) {
            this.model = new KhThemeServiceModel();
        }
    }

    setTheme(app: App): void {
        this.name = app.name;
        this._updateFile(app, 'primaryLogo');
        this._updateFile(app, 'secondaryLogo');
        this._updateFile(app, 'mapPin');
        this._updateThemeColor(app, "primaryColor");
        this.primaryTextColor = this.getPrimaryTextColorFormattedAsHexStringWithHashtagForApp(app);
        this._updateThemeColor(app, "secondaryColor");
        this._updateThemeColor(app, "secondaryTextColor");
        this.mapPinHeight = app.mapPinHeight ?? 25;
        this.mapPinWidth = app.mapPinWidth ?? 25;
        this.mapPinStyle = app.mapPinStyle ?? MapPinStyles.Default;
        this.mapPinColor = app.mapPinColor ?? new Color(234, 67, 53, 255);
        this.color.next(this.primaryColor);
        this.setMapIcon();

        this.setSCSSVariables();

        this._mapSettingsDataService.loadSVG()
            .then(svgDocument => {
                this.svgDocument = svgDocument;
                this.svg = this.colorizeSVG();
                this.setMapIcon();
            });
    }

    resetTheme(): void {
        this.name = environment.name;
        this.primaryColor = environment.colors.primaryColor;
        this.primaryTextColor = environment.colors.primaryTextColor;
        this.primaryLogo = environment.url + environment.logos.primaryLogo;
        this.secondaryColor = environment.colors.secondaryColor;
        this.secondaryTextColor = environment.colors.secondaryTextColor;
        this.secondaryLogo = environment.url + environment.logos.secondaryLogo;
        this.mapPinHeight = 25;
        this.mapPinWidth = 25;
        this.mapPinStyle = MapPinStyles.Default;
        this.mapPinColor = new Color(234, 67, 53, 255);
        this.mapPin = null;
        this.color.next(environment.colors.primaryColor);

        this.setSCSSVariables();
    }

    getPrimaryTextColorFormattedAsHexStringWithHashtagForApp(app: App): string {
        let primaryColorObject = app.primaryColor;
        if (!primaryColorObject) {
            primaryColorObject = this.hexToColorObject(environment.colors.primaryColor);
        }
        const primaryTextColorObject = this.calculateTextColorForBgColor(primaryColorObject);
        let hex = primaryTextColorObject.hex;
        if (!hex) {
            hex = this.defaultTextColor.hex;
        }
        return '#' + hex;
    }

    isValidHexString(hex: string): boolean {
        return /^#([0-9a-f]{3}){1,2}$/i.test(hex);
    }

    hexToColorObject(hexString: string): Color {
        // Todo: replace references to this function with colorsUtilityService.hexToColorObject
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexString);
        return result ? new Color(
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
        ) : null;
    }

    calculateContentHeight(padding = 156): void {
        this.gridHeight = window.innerHeight - 290;
        const paddedHeight = environment.outerHeight + padding;
        if (window.innerHeight <= paddedHeight) {
            this.contentHeight.next(paddedHeight + 20);
        }
        else {
            this.contentHeight.next(window.innerHeight - environment.outerHeight + 20);
        }
    }

    setMapIcon(): void {
        const scaledSize = new google.maps.Size(this.mapPinWidth, this.mapPinHeight);

        if (this.mapPinStyle === MapPinStyles.Default || !this.mapPin) {
            this.model.mapIcon = new MapIcon(this.svg, scaledSize);
            return;
        }
        this.model.mapIcon = new MapIcon(this.mapPin, scaledSize);
    }

    getMapPinScaledSize(): google.maps.Size {
        return new google.maps.Size(this.mapPinWidth, this.mapPinHeight);
    }

    colorizeSVG(): string {
        const secondaryMapPinColor = this.get40PercentDarkerSecondaryColor(this.mapPinColor);
        return this.buildColorizedSvgFromColor(this.mapPinColor, secondaryMapPinColor);
    }

    buildColorizedSvgFromHex(hex: string): string {
        const hexAsColor: Color = this.hexToColorObject(hex);
        const secondaryColor = this.get40PercentDarkerSecondaryColor(hexAsColor);

        return this.buildColorizedSvgFromColor(hexAsColor, secondaryColor);
    }

    get40PercentDarkerSecondaryColor(color: Color): Color {
        const hsl = this.rgbToHueSaturationLightnessFormat(color.r, color.g, color.b);
        return this.hueSaturationLightnessToColor(hsl.hue, hsl.saturation, hsl.lightness - 0.4);
    }

    buildColorizedSvgFromColor(color: Color, secondaryColor: Color): string {
        if (!this.svgDocument) {
            return this.svg;
        }

        const styleElement = this.svgDocument.querySelector('style');

        if (styleElement) {
            styleElement.textContent = `.icon-fill { fill: ${color.rgba}; stroke: ${secondaryColor.rgba}; stroke-width: 1px; }`;
        }

        const svgString = new XMLSerializer().serializeToString(this.svgDocument.documentElement);
        return `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;
    }

    calculateLuminanceOfColorFast(rgb: Color): number {
        return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    }

    private sRGBtoLinear(colorChannel: number): number {
        // Send this function a decimal sRGB gamma encoded color value
        // between 0.0 and 1.0, and it returns a linearized value.
        const gamma = 2.4;
        if (colorChannel <= 0.04045) {
            return colorChannel / 12.92;
        }
        return Math.pow(((colorChannel + 0.055) / 1.055), gamma);
    }

    calculateLuminanceOfColor(color: Color): number {
        const vR = color.r / 255;
        const vG = color.g / 255;
        const vB = color.b / 255;
        return (0.2126 * this.sRGBtoLinear(vR) + 0.7152 * this.sRGBtoLinear(vG) + 0.0722 * this.sRGBtoLinear(vB));
    }

    calculatePerceivedLightnessOfColor(color: Color): number {
        const luminance = this.calculateLuminanceOfColor(color);
        return this.calculatePerceivedLightnessOfLuminance(luminance);
    }

    calculateTextColorForBgColor(bgColor: Color): Color {
        const bgPerceivedLightness = this.calculatePerceivedLightnessOfColor(bgColor);
        const maxBgPerceivedLightnessForLightText = 68.5;
        if (bgPerceivedLightness > maxBgPerceivedLightnessForLightText) {
            return this.darkTextColor;
        }
        return this.lightTextColor;
    }

    primaryFiftyOpacity(): string {
        return this.primaryColor + '50';
    }

    primarySevenOpacity(): string {
        return this.primaryColor + '07';
    }

    /**
     * Switches between light and dark theme modes.
     * Updates Material 3 theme tokens for the active mode.
     * @param isDark - true for dark theme, false for light theme
     */
    switchThemeMode(isDark: boolean): void {
        this._isDarkMode = isDark;
        if (isDark) {
            document.body.classList.add('theme-alternate');
        }
        else {
            document.body.classList.remove('theme-alternate');
        }
        this.setSCSSVariables();
    }

    /**
     * Returns current theme mode.
     * @returns true if dark mode is active, false for light mode
     */
    isDarkMode(): boolean {
        return this._isDarkMode;
    }

    /**
     * Injects runtime theme variables into :root.
     *
     * Source of truth:
     * - Material 3 tokens are the canonical theme contract for component colors.
     * - Legacy KHTS variables are still emitted only for backward compatibility.
     *
     * Runtime flow:
     * 1) Start from app-configured primary hex (`this.primaryColor`).
     * 2) Build light/dark Material schemes with Material Color Utilities.
     * 3) Inject M3 system tokens (`--mat-sys-*`) and mat alias tokens (`--mat-*`) used by components.
     * 4) Keep legacy KHTS variables (`--theme-color*`) in sync.
     *
     * Legacy -> M3 mapping used by this service:
     * - `--theme-color` -> `--mat-sys-primary`
     * - `--theme-color-fifty-opacity` -> color-mix based primary opacity helper
     * - `--theme-color-seven-opacity` -> color-mix based primary opacity helper
     *
     * Override guidance:
     * - Prefer setting app color inputs (`primaryColor`, `primaryTextColor`) and let token generation cascade.
     * - If a component still needs explicit runtime overrides, add aliases in `_injectMaterialAliasTokens()`.
     * - Avoid per-component hardcoded colors when an existing `--mat-sys-*` or `--mat-*` token can be used.
     */
    private setSCSSVariables(): void {
        // Generate Material 3 theme from primary hex color
        // Material Color Utilities converts hex to ARGB, generates tone palette
        try {
            const argbPrimary = argbFromHex(this.primaryColor);
            const theme = themeFromSourceColor(argbPrimary, [
                {
                    name: 'custom_color_1',
                    value: argbPrimary,
                    blend: true,
                }
            ]);

            // Get the appropriate theme (light or dark)
            const schemeConfig = this._isDarkMode ? theme.schemes.dark : theme.schemes.light;

            // Inject legacy KHTS variables for backward compatibility
            this._root.style.setProperty(CSSCalculatedVariables.ThemeColor, this.primaryColor);
            this._root.style.setProperty(CSSCalculatedVariables.ThemeColorFiftyOpacity, this.primaryFiftyOpacity());
            this._root.style.setProperty(CSSCalculatedVariables.ThemeColorSevenOpacity, this.primarySevenOpacity());

            // Inject Material 3 system tokens into CSS custom properties
            // These will be consumed by Material components automatically
            this._injectMatSysThemeTokens(schemeConfig);
        }
        catch (error) {
            console.error('Error generating Material 3 theme tokens:', error);
            // Fallback: inject default M3 tokens with primary color
            this._injectDefaultMatSysThemeTokens();
        }
    }

    private _setCssVariables(variables: Record<string, string>): void {
        Object.entries(variables).forEach(([variableName, value]) => {
            this._root.style.setProperty(variableName, value);
        });
    }

    /**
     * Injects Material 3 system tokens (`--mat-sys-*`) from a Material Color Utilities scheme.
     *
     * Surface-family tokens are intentionally derived from a zero-chroma neutral ramp
     * rather than from `scheme.surface`. Material's `tonalSpot` scheme tints its neutral
     * palette with the source hue, which — for the brand's purple primary — produces a
     * faintly purple surface (e.g. #fffbff). Building surfaces from a pure-gray palette
     * keeps them truly neutral while primary / secondary / tertiary / error stay brand-derived.
     *
     * @param scheme The color scheme (light or dark) from Material Color Utilities
     */
    private _injectMatSysThemeTokens(scheme: Scheme): void {
        const isDark = this._isDarkMode;

        // Neutral (chroma 0) ramp so surfaces carry no purple tint.
        const neutral = TonalPalette.fromHueAndChroma(0, 0);
        const surface = this._argbToHex(neutral.tone(isDark ? 6 : 100));
        const onSurface = this._argbToHex(neutral.tone(isDark ? 90 : 10));
        const surfaceVariant = this._argbToHex(neutral.tone(isDark ? 30 : 90));
        const onSurfaceVariant = this._argbToHex(neutral.tone(isDark ? 80 : 30));
        const outline = this._argbToHex(neutral.tone(isDark ? 60 : 50));
        const outlineVariant = this._argbToHex(neutral.tone(isDark ? 30 : 80));

        this._setCssVariables({
            // Primary (use Material Color Utilities generated values, not raw brand color)
            '--mat-sys-primary': this._argbToHex(scheme.primary),
            '--mat-sys-on-primary': this._argbToHex(scheme.onPrimary),
            '--mat-sys-primary-container': this._argbToHex(scheme.primaryContainer),
            '--mat-sys-on-primary-container': this._argbToHex(scheme.onPrimaryContainer),

            // Surface (neutral, untinted)
            '--mat-sys-surface': surface,
            '--mat-sys-on-surface': onSurface,
            '--mat-sys-surface-variant': surfaceVariant,
            '--mat-sys-on-surface-variant': onSurfaceVariant,

            // Outline (neutral)
            '--mat-sys-outline': outline,
            '--mat-sys-outline-variant': outlineVariant,

            // Error
            '--mat-sys-error': this._argbToHex(scheme.error),
            '--mat-sys-on-error': this._argbToHex(scheme.onError),
            '--mat-sys-error-container': this._argbToHex(scheme.errorContainer),
            '--mat-sys-on-error-container': this._argbToHex(scheme.onErrorContainer),
        });

        // Secondary colors (if available)
        if (scheme.secondary) {
            this._setCssVariables({
                '--mat-sys-secondary': this._argbToHex(scheme.secondary),
                '--mat-sys-on-secondary': this._argbToHex(scheme.onSecondary),
                '--mat-sys-secondary-container': this._argbToHex(scheme.secondaryContainer),
                '--mat-sys-on-secondary-container': this._argbToHex(scheme.onSecondaryContainer),
            });
        }

        // Tertiary colors (if available)
        if (scheme.tertiary) {
            this._setCssVariables({
                '--mat-sys-tertiary': this._argbToHex(scheme.tertiary),
                '--mat-sys-on-tertiary': this._argbToHex(scheme.onTertiary),
                '--mat-sys-tertiary-container': this._argbToHex(scheme.tertiaryContainer),
                '--mat-sys-on-tertiary-container': this._argbToHex(scheme.onTertiaryContainer),
            });
        }

        // Scrim (overlay) colors
        if (scheme.scrim) {
            this._root.style.setProperty('--mat-sys-scrim', this._argbToHex(scheme.scrim));
        }
    }

    /**
     * Fallback: Injects default Material 3 system tokens when dynamic generation fails.
     * Uses the primary color with neutral, untinted surface defaults.
     */
    private _injectDefaultMatSysThemeTokens(): void {
        // Set text color based on background luminance
        const primaryColorObj = this.hexToColorObject(this.primaryColor);
        const textColor = this.calculateTextColorForBgColor(primaryColorObj);

        this._setCssVariables({
            '--mat-sys-primary': this.primaryColor,
            '--mat-sys-on-primary': '#' + textColor.hex,
            // Neutral, untinted surface defaults (light theme).
            '--mat-sys-surface': '#ffffff',
            '--mat-sys-on-surface': '#1c1b1f',
            '--mat-sys-error': '#b3261e',
            '--mat-sys-on-error': '#ffffff',
        });
    }

    /**
     * Returns the Material 3 light-scheme primary hex derived from a source Color.
     * Equivalent to the tone-40 primary the scheme generates — guaranteed contrast
     * with `getOnPrimaryFromSourceColor`.
     * Returns empty string when color is falsy so callers can supply a CSS fallback.
     */
    getPrimaryFromSourceColor(color: Color): string {
        if (!color) {
            return '';
        }
        try {
            const argb = argbFromHex(color.hex);
            return this._argbToHex(themeFromSourceColor(argb).schemes.light.primary);
        }
        catch {
            return '';
        }
    }

    /**
     * Returns the Material 3 light-scheme on-primary hex derived from a source Color.
     * This is the accessible text / icon color to pair with `getPrimaryFromSourceColor`.
     * Returns empty string when color is falsy so callers can supply a CSS fallback.
     */
    getOnPrimaryFromSourceColor(color: Color): string {
        if (!color) {
            return '';
        }
        try {
            const argb = argbFromHex(color.hex);
            return this._argbToHex(themeFromSourceColor(argb).schemes.light.onPrimary);
        }
        catch {
            return '';
        }
    }

    /**
     * Converts ARGB color value (from Material Color Utilities) to hex string.
     * @param argb - ARGB color value
     * @returns Hex string without '#' prefix
     */
    private _argbToHex(argb: number): string {
        const hex = argb.toString(16);
        // Ensure proper padding, remove alpha channel (first 2 digits if ARGB)
        return '#' + hex.slice(-6).padStart(6, '0');
    }

    private rgbToHueSaturationLightnessFormat(r: number, g: number, b: number): HSLColor {
        r /= 255;
        g /= 255;
        b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let hue, saturation, lightness = (max + min) / 2;

        if (max == min) {
            hue = saturation = 0;
            return { hue, saturation, lightness };
        }

        const diff = max - min;
        saturation = lightness > 0.5 ? diff / (2 - max - min) : diff / (max + min);
        switch (max) {
            case r: hue = (g - b) / diff + (g < b ? 6 : 0);
                break;
            case g: hue = (b - r) / diff + 2;
                break;
            case b: hue = (r - g) / diff + 4;
                break;
        }
        hue /= 6;

        return { hue, saturation, lightness };
    }

    private hueSaturationLightnessToColor(hue: number, saturation: number, lightness: number): Color {
        let r, g, b;

        if (saturation === 0) {
            r = g = b = lightness;
            return new Color(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255), 1);
        }

        const lightnessRange = lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation;
        const lightnessAdjustment = 2 * lightness - lightnessRange;
        r = this.hueToRgb(lightnessAdjustment, lightnessRange, hue + 1 / 3);
        g = this.hueToRgb(lightnessAdjustment, lightnessRange, hue);
        b = this.hueToRgb(lightnessAdjustment, lightnessRange, hue - 1 / 3);

        return new Color(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255), 1);
    }

    private hueToRgb(lightnessAdjustment: number, lightnessRange: number, hue: number): number {
        if (hue < 0) {
            hue += 1;
        }
        if (hue > 1) {
            hue -= 1;
        }
        if (hue < 1 / 6) {
            return lightnessAdjustment + (lightnessRange - lightnessAdjustment) * 6 * hue;
        }
        if (hue < 1 / 2) {
            return lightnessRange;
        }
        if (hue < 2 / 3) {
            return lightnessAdjustment + (lightnessRange - lightnessAdjustment) * (2 / 3 - hue) * 6;
        }
        return lightnessAdjustment;
    }

    private calculatePerceivedLightnessOfLuminance(luminance): number {
        // Send this function a luminance value between 0.0 and 1.0,
        // and it returns perceptual lightness
        if (luminance <= (216 / 24389))        // The CIE standard states 0.008856 but 216/24389 is the intent for 0.008856451679036
        {
            return luminance * (24389 / 27);
        }  // The CIE standard states 903.3, but 24389/27 is the intent, making 903.296296296296296
        return Math.pow(luminance, (1 / 3)) * 116 - 16;
    }

    private _updateFile(app: App, field: string): void {
        if (app[field] === undefined || app[field] === null) {
            this[field] = null;
        }
        else {
            this[field] = this._generatePublicUrl(app[field]);
        }
    }

    private _updateThemeColor(app: App, field: string): void {
        if (app !== null && app[field] !== undefined) {
            this[field] = "#" + app[field].hex;
        }
        else {
            this[field] = environment.colors[field];
        }
    }

    private _generatePublicUrl(fileInfo: File): string {
        if (!fileInfo?.fileId || !fileInfo?.containerName) {
            return fileInfo?.uri;
        }

        const containerName = fileInfo.containerName;
        const baseRoute = environment.api + 'public-file';
        return `${baseRoute}/${containerName}/${fileInfo.fileId}`;
    }
}
